import axios from 'axios';

// Configure axios - normalize API URLs in one place
const hostname = window.location.hostname.toLowerCase();
const isProduction = hostname === 'patelhrms.com' 
  || hostname.includes('patelhrms.com')
  || hostname === 'vitreous.support'
  || hostname.includes('vitreous.support')
  || hostname === 'www.vitreous.support';
const CANONICAL_API_ORIGIN = 'https://vitreous.support';
const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

// In production, prefer the canonical API host so login/API do not depend on
// per-host reverse-proxy bindings (which can cause intermittent 502 on aliases).
const API_ORIGIN = isProduction ? CANONICAL_API_ORIGIN : '';

// Debug logging
console.log('[API Config] Hostname:', hostname, '| Is Production:', isProduction, '| API Origin:', API_ORIGIN);

// Create axios instance WITHOUT baseURL - URLs are normalized in interceptor
const api = axios.create({
  baseURL: '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and normalize URL
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Normalize API path to always include /api prefix.
    if (config.url) {
      // Skip absolute URLs (eg external file uploads or explicit full URLs).
      if (!isAbsoluteUrl(config.url)) {
        let path = config.url;

        // Normalize path: remove leading /api if present to avoid /api/api.
        if (path.startsWith('/api/')) {
          path = path.substring(4);
        } else if (path.startsWith('/api')) {
          path = path.substring(4);
        }

        if (!path.startsWith('/')) {
          path = '/' + path;
        }

        path = '/api' + path;
        config.url = API_ORIGIN + path;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // One-time retry on 502 against canonical API host in production.
    // Helps when the current hostname's reverse proxy is unhealthy.
    if (
      isProduction &&
      error?.response?.status === 502 &&
      originalRequest &&
      !originalRequest._retryOnCanonicalHost
    ) {
      try {
        const originalUrl: string = originalRequest.url || '';
        const targetOnCanonical = isAbsoluteUrl(originalUrl) && originalUrl.startsWith(CANONICAL_API_ORIGIN);

        if (!targetOnCanonical) {
          const retryPath = isAbsoluteUrl(originalUrl)
            ? originalUrl.replace(/^https?:\/\/[^/]+/i, '')
            : originalUrl;

          originalRequest._retryOnCanonicalHost = true;
          originalRequest.url = `${CANONICAL_API_ORIGIN}${retryPath}`;
          return api(originalRequest);
        }
      } catch (retryError) {
        console.error('[API Config] Canonical host retry failed:', retryError);
      }
    }

    if (error.response?.status === 401) {
      // Don't redirect if we're on TV Dashboard - it has its own auth
      const isTVDashboard = window.location.pathname === '/tvdashboard' || window.location.pathname.startsWith('/tvdashboard');
      
      if (!isTVDashboard) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          // Preserve /chat so user returns to chat after re-login
          const returnUrl = window.location.pathname === '/chat' ? '?returnUrl=' + encodeURIComponent('/chat') : '';
          window.location.href = '/login' + returnUrl;
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
